import "./index.css";

import React from "react";
import BaseComponent, {executeComponentCallback} from 'Core/components/BaseComponent';
import PropTypes from "prop-types";
import {startOfDay, setHours, setMinutes, setSeconds, format} from 'date-fns';
import {getTimePickerFormatFromDatetimeFormat} from 'Core/components/input/DatetimeInput/components/TimePicker/helper';
import {cloneDeep} from 'lodash';
import Button, {BUTTON_DISPLAY_TYPE} from 'Core/components/display/Button';
import {STANDARD_DATE_TIME_FORMAT} from 'Core/const/datetime';

class DatetimeInputTimePicker extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: 'DatetimeInputTimePicker',
			domPrefix: 'datetime-input-time-picker',
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onChange'],
			domManipulationIntervalTimeout: 1,
		});
	}

	
	// DOM manipulation interval methods --------------------------------------------------------------------------------
	/** @inheritDoc */
	domManipulations(element) {
		// Move the time picker dropdown to the same position as the clock element
		const clockElement = element.closest('.react-datetime-picker').querySelector('.react-datetime-picker__clock');
		if (!!element && !!clockElement) {
			element.style.position = clockElement.style.position;
			element.style.inset = clockElement.style.inset;
			element.style.height = clockElement.style.height;
		}
	}
	

	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Handle option click event
	 *
	 * @param {string} optionKey - Key of the option that was clicked
	 * @param {string} selectedValue - Selected value
	 */
	handleOptionChange(optionKey, selectedValue) {
		const currentValue = this.props.value || new Date();
		let nextValue = cloneDeep(currentValue);

		switch (optionKey) {
			case 'h':
			case 'hh':
			case 'H':
			case 'HH':
			case 'K':
			case 'KK':
			case 'k':
			case 'kk':
				nextValue = setHours(nextValue, parseInt(selectedValue, 10));
				break;

			case 'm':
			case 'mm':
				nextValue = setMinutes(nextValue, parseInt(selectedValue, 10));
				break;

			case 's':
			case 'ss':
				nextValue = setSeconds(nextValue, parseInt(selectedValue, 10));
				break;

			default:
				break;
		}

		executeComponentCallback(this.props.onChange, nextValue);
	}

	/**
	 * Get current value for a specific time segment
	 *
	 * @param {string} optionKey - Time segment key
	 * @return {string}
	 */
	getSelectedValue(optionKey) {
		const {value} = this.props;

		if (!(value instanceof Date)) return '';
		if (['h', 'hh', 'H', 'HH', 'K', 'KK', 'k', 'kk'].includes(optionKey)) return format(value, optionKey);
		if (['m', 'mm'].includes(optionKey)) return format(value, optionKey);
		if (['s', 'ss'].includes(optionKey)) return format(value, optionKey);
		return '';
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {className, dateFormat, hoursStep, minutesStep, secondsStep, readOnly} = this.props;
		const timePickerOptions = getTimePickerFormatFromDatetimeFormat(dateFormat)
			// Filter options based on specified increment step
			.map(option => {
				const step = (
					['h', 'hh', 'H', 'HH', 'K', 'KK', 'k', 'kk'].includes(option.key) ? hoursStep : 
					['m', 'mm'].includes(option.key) ? minutesStep :  
					['s', 'ss'].includes(option.key) ? secondsStep : 
					1
				);
				return {...option, value: option.value.filter((_, index) => index % step === 0)};
			});
		

		return (
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')} ${className}`}>
				{timePickerOptions.map(option => {
					const selectedValue = this.getSelectedValue(option.key);

					return (
						<div key={option.key} className="group">
							<div className="list">
								{option.value.map(timeOption => (
									<Button
										key={timeOption}
										className={
											`item ${timeOption === selectedValue ? 'selected' : ''} ` +
											`${readOnly ? 'read-only' : ''}`
										}
										label={timeOption}
										displayType={BUTTON_DISPLAY_TYPE.NONE}
										onClick={(!readOnly ? () => this.handleOptionChange(option.key, timeOption) : undefined)}
									/>
								))}
							</div>
						</div>
					);
				})}
			</div>
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
DatetimeInputTimePicker.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,

	value: PropTypes.instanceOf(Date),
	dateFormat: PropTypes.string,
	hoursStep: PropTypes.number,
	minutesStep: PropTypes.number,
	secondsStep: PropTypes.number,
	readOnly: PropTypes.bool,

	/**
	 * Callback function triggered when the selected time changes
	 * @type {Function<Date>}
	 */
	onChange: PropTypes.func,
};

/**
 * Define component default values for own props
 */
DatetimeInputTimePicker.defaultProps = {
	id: '',
	className: '',
	value: new startOfDay(new Date()),
	dateFormat: STANDARD_DATE_TIME_FORMAT.TIME,
	hoursStep: 1,
	minutesStep: 1,
	secondsStep: 1,
	readOnly: false,
};

export * from './const';
export * from './helper';
export default DatetimeInputTimePicker;